import { bookingInquiryStrings } from '@data/bookingInquiry';
import { contactStrings } from '@data/contacts';
import { instructionStrings } from '@data/instruction';
import { invoiceStrings } from '@data/invoice';
import { ledgerStrings } from '@data/ledger';
import { routePlanStrings } from '@data/routePlan';
import { Country } from '@models/next/Country';
import { LedgerAccount } from '@models/next/LedgerAccount';
import { Currency } from '@models/next/Money';
import { PaginationType } from '@models/next/PaginationType';
import { AdminUser } from '@models/next/User';
import { f } from '@utils/nextForms';
import { z } from 'zod';

export const InvoiceStatusName = z.enum(['作成中', '修正中', '発行済']);

export type InvoiceStatusName = z.infer<typeof InvoiceStatusName>;

export const InvoicePaymentStatus = z.enum(['済', '未']);

export type InvoicePaymentStatus = z.infer<typeof InvoicePaymentStatus>;

export const InvoiceLanguage = z.enum(['ENGLISH', '日本語']);

export type InvoiceLanguage = z.infer<typeof InvoiceLanguage>;

export const NonEmptyExciseTaxType = z.enum(['非課税', '課税8%', '課税10%']);

export type NonEmptyExciseTaxType = z.infer<typeof NonEmptyExciseTaxType>;

export const ExciseTaxType = z.enum(['', ...NonEmptyExciseTaxType.options]);

export type ExciseTaxType = z.infer<typeof ExciseTaxType>;

export const InvoiceDetailsCountry = z.enum(['cn', 'jp', 'none']);

export type InvoiceDetailsCountry = z.infer<typeof InvoiceDetailsCountry>;

export const InvoiceDetailsUnitNumType = z.enum(['B/L', 'JOB', 'M3']);

export type InvoiceDetailsUnitNumType = z.infer<typeof InvoiceDetailsUnitNumType>;

export const InvoiceStatus = z.object({
  name: InvoiceStatusName,
  time: z.string(),
  message: z.string(),
});

export type InvoiceStatus = z.infer<typeof InvoiceStatus>;

export const InvoiceDetailsFields = z.object({
  id: z.number(),
  expense_cost: z.number(),
  invoice_name: z.string().label(invoiceStrings.invoice_name),
  unit_num_type: InvoiceDetailsUnitNumType,
  currency: Currency,
  unit_price: z.coerce.number().nullish(),
  unit_num: z.coerce.number().nullish(),
  excise_tax_type: ExciseTaxType.label(invoiceStrings.excise_tax),
  country: InvoiceDetailsCountry,
  payer_mark: Country.label(invoiceStrings.payer_mark).formMeta({
    getInitialOutput: () => 'jp',
  }),
  debit: LedgerAccount,
  credit: LedgerAccount,
  invoice_name_preparation: z.string().label(invoiceStrings.invoice_name_preparation),
  updated_at: z.string(),
  created_at: z.string(),
  invoice: z.number().nullish(),
  is_locked: z.boolean(),
});

export type InvoiceDetailsFields = z.infer<typeof InvoiceDetailsFields>;

export const NumericInvoiceDetails = InvoiceDetailsFields.pick({
  currency: true,
  unit_price: true,
  unit_num: true,
  excise_tax_type: true,
});

export type NumericInvoiceDetails = z.infer<typeof NumericInvoiceDetails>;

export const InvoiceDetails = InvoiceDetailsFields.pick({
  id: true,
  expense_cost: true,
  invoice_name: true,
  unit_num_type: true,
  currency: true,
  unit_price: true,
  unit_num: true,
  excise_tax_type: true,
  country: true,
  payer_mark: true,
  debit: true,
  credit: true,
  invoice_name_preparation: true,
  updated_at: true,
  created_at: true,
  invoice: true,
  is_locked: true,
});

export type InvoiceDetails = z.infer<typeof InvoiceDetails>;

export const PostInvoiceDetails = InvoiceDetailsFields.pick({
  expense_cost: true,
  invoice_name: true,
  unit_num_type: true,
  currency: true,
  unit_price: true,
  unit_num: true,
  excise_tax_type: true,
  payer_mark: true,
  debit: true,
  credit: true,
  invoice_name_preparation: true,
});

export type PostInvoiceDetails = z.infer<typeof PostInvoiceDetails>;

export const PatchInvoiceDetails = InvoiceDetailsFields.pick({
  expense_cost: true,
  invoice_name: true,
  unit_num_type: true,
  currency: true,
  unit_price: true,
  unit_num: true,
  excise_tax_type: true,
  payer_mark: true,
  debit: true,
  credit: true,
  invoice_name_preparation: true,

  is_locked: true,
}).partial();

export type PatchInvoiceDetails = z.infer<typeof PatchInvoiceDetails>;

export const InvoiceDetailsForm = z.object({
  invoice_name: InvoiceDetailsFields.shape.invoice_name.min(1),
  unit_num_type: InvoiceDetailsFields.shape.unit_num_type,
  currency: InvoiceDetailsFields.shape.currency,
  unit_price: z.number().label(invoiceStrings.unit_price),
  unit_num: z.number().label(invoiceStrings.unit_num),
  excise_tax_type: NonEmptyExciseTaxType.label(invoiceStrings.excise_tax),
  payer_mark: InvoiceDetailsFields.shape.payer_mark,
  debit: InvoiceDetailsFields.shape.debit.label(ledgerStrings.division),
  invoice_name_preparation: InvoiceDetailsFields.shape.invoice_name_preparation,
});

export type InvoiceDetailsForm = z.infer<typeof InvoiceDetailsForm>;

export const InvoiceFields = z.object({
  house_bl_number: z.string().label(invoiceStrings.house_bl_number),
  charge_date: f.dateString().label(invoiceStrings.charge_date),
  client_id: z.number().label(invoiceStrings.client_id),
  client_code: z.string(), // === company.member_no
  client_name: z.string().label(invoiceStrings.client_id),
  client_name_en: z.string(), // === company.english_name
  client_zipcode: z.string().label(contactStrings.zip_code),
  client_address1: z.string().label(contactStrings.address1),
  client_address2: z.string().label(contactStrings.address2),
  payer_mark: Country.label(invoiceStrings.payer_mark),

  invoice_no: z.string().label(invoiceStrings.invoice_no), // kikan INV NO.
  job_code: z.string().label(invoiceStrings.job_code), // kikan 台帳番号
  charge_pay_day: f.dateString().label(invoiceStrings.charge_pay_day), // kikan 支払期限

  vessel_voyage: z.string().label(invoiceStrings.vessel_voyage),
  transport_route: z.string().label(invoiceStrings.transport_route),
  ata: f.dateString().label(invoiceStrings.ata).nullable(),
  quantity_packages: z.string().label(invoiceStrings.quantity_packages),
  customer_no: z.string().label(invoiceStrings.customer_no),
  conversion_rate: f.decimal().label(invoiceStrings.conversion_rate),
  conversion_rate_date: f.dateString(),
  language: InvoiceLanguage.label(invoiceStrings.language),
  invoice_status: InvoiceStatusName.label(invoiceStrings.invoice_status),
  status: InvoiceStatus.array(),
  salesperson_name: z.string(),

  id: z.number(),
  visit_id: z.string(),

  // FC-managed fields
  charge_no: z.string().nullish().label(invoiceStrings.charge_no),
  payment_date: f.dateString().nullish().label(invoiceStrings.payment_date),
  finish_date: f.dateString().nullish().label(invoiceStrings.finish_date),
  payment_status: InvoicePaymentStatus.label(invoiceStrings.payment_status),

  // D2D-managed state fields
  d2d_charge_no: z.string().label(invoiceStrings.d2d_charge_no),
  review_time: z.string().nullish(), // Time of 修正中
  send_time: z.string().nullish(), // Time of 発行済
  total_price: f.decimal().label(invoiceStrings.total_price),
  total_price_currency: Currency,
  booking_inquiry: z.number(),

  member_salesperson_last_name: z.string().nullish(),
  salesperson_japan_last_name: z.string().nullish(),
  shipper_name: z.string().nullish().label('SHIPPER'),
  consignee_name: z.string().nullish().label('CNEE'),
  preferred_arrival_date: f.dateString().nullish().label(instructionStrings.preferredDeliveryDate),
  quantity: z.number().nullish().label(invoiceStrings.quantity),
  receiver_company_name: z.string().nullish().label(bookingInquiryStrings.receiver),
  service_name: z.string().nullish().label(routePlanStrings.serviceName),

  creator: AdminUser,

  // Subsums
  tax_excluded_total_price: z.number(),
  excise_tax: z.number(),
  tatekae_total_price: z.number(),

  invoice_id_list: z.number().array(),

  created_at: z.string(),
  updated_at: z.string(),
});

export const Invoice = InvoiceFields.pick({
  house_bl_number: true,
  charge_date: true,
  client_id: true,
  client_code: true,
  client_name: true,
  client_name_en: true,
  client_zipcode: true,
  client_address1: true,
  client_address2: true,
  payer_mark: true,

  invoice_no: true, // kikan INV NO.
  job_code: true, // kikan 台帳番号
  charge_pay_day: true, // kikan 支払期限

  vessel_voyage: true,
  transport_route: true,
  ata: true,
  quantity_packages: true,
  customer_no: true,
  conversion_rate: true,
  conversion_rate_date: true,
  language: true,
  invoice_status: true,
  status: true,
  salesperson_name: true,

  id: true,
  visit_id: true,

  // FC-managed fields
  charge_no: true,
  payment_date: true,
  finish_date: true,
  payment_status: true,

  // D2D-managed state fields
  d2d_charge_no: true,
  review_time: true, // Time of 修正中
  send_time: true, // Time of 発行済
  total_price: true,
  total_price_currency: true,
  booking_inquiry: true,
  member_salesperson_name: true,
  member_salesperson_last_name: true,
  salesperson_japan_last_name: true,
  shipper_name: true,
  consignee_name: true,
  preferred_arrival_date: true,
  quantity: true,
  receiver_company_name: true,
  service_name: true,

  creator: true,

  // Subsums
  tax_excluded_total_price: true,
  excise_tax: true,
  tatekae_total_price: true,

  created_at: true,
  updated_at: true,
});

export type Invoice = z.infer<typeof Invoice>;

export const InvoicePages = z.object({
  count: z.number(),
  results: Invoice.array(),
});

export type InvoicePages = z.infer<typeof InvoicePages>;

export const DashboardInvoiceSearchQuery = z
  .object({
    page: PaginationType.shape.page,
    page_size: PaginationType.shape.page_size,
    booking_inquiry: InvoiceFields.shape.booking_inquiry,
    // house_bl_number: InvoiceFields.shape.house_bl_number,
    d2d_charge_no: InvoiceFields.shape.d2d_charge_no,
    ordering: z.string().array(),
    charge_date_before: InvoiceFields.shape.charge_date,
    charge_date_after: InvoiceFields.shape.charge_date,
    charge_pay_day_before: InvoiceFields.shape.charge_pay_day,
    charge_pay_day_after: InvoiceFields.shape.charge_pay_day,
    payment_status: InvoiceFields.shape.payment_status,
    // salesperson_name: t.array(t.string), ?? not clear the usage
    invoice_status: InvoiceFields.shape.invoice_status,
    member_salesperson_id: z.number(),
    salesperson_japan_id: z.number(),
    company_name: z.string().label(invoiceStrings.client_id),
    customer_no: InvoiceFields.shape.customer_no,
    shipper_name: InvoiceFields.shape.shipper_name,
    consignee_name: InvoiceFields.shape.consignee_name,
    quantity: InvoiceFields.shape.quantity,
    service_name: InvoiceFields.shape.service_name,
    total_price_min: InvoiceFields.shape.total_price,
    total_price_max: InvoiceFields.shape.total_price,
    total_price_currency: Currency,
    preferred_arrival_date_before: InvoiceFields.shape.preferred_arrival_date,
    preferred_arrival_date_after: InvoiceFields.shape.preferred_arrival_date,
    d2d_no: z.string().label(invoiceStrings.d2d_no),
    job_code: InvoiceFields.shape.job_code,
    member_salesperson: z.string().label(invoiceStrings.member_salesperson),
    salesperson_japan: z.string().label(invoiceStrings.salesperson_japan),
  })
  .partial();

export type DashboardInvoiceSearchQuery = z.infer<typeof DashboardInvoiceSearchQuery>;

export const InvoiceDetailsQuery = z.object({
  show_all: z.boolean(),
});

export type InvoiceDetailsQuery = z.infer<typeof InvoiceDetailsQuery>;

export const InvoiceCount = z.object({
  count: z.number(),
});

export type InvoiceCount = z.infer<typeof InvoiceCount>;

export const InvoiceDefaultValue = z.object({
  house_bl_number: InvoiceFields.shape.house_bl_number,
  charge_date: InvoiceFields.shape.charge_date,
  job_code: InvoiceFields.shape.job_code,
  vessel_voyage: InvoiceFields.shape.vessel_voyage,
  transport_route: InvoiceFields.shape.transport_route,
  ata: InvoiceFields.shape.ata,
  quantity_packages: InvoiceFields.shape.quantity_packages,
  salesperson_name: InvoiceFields.shape.salesperson_name,
  conversion_rate: InvoiceFields.shape.conversion_rate.nullable(),
  conversion_rate_date: InvoiceFields.shape.conversion_rate_date.nullable(),
  customer_no: InvoiceFields.shape.customer_no,
});

export type InvoiceDefaultValue = z.infer<typeof InvoiceDefaultValue>;

export const PostInvoice = z.object({
  house_bl_number: InvoiceFields.shape.house_bl_number,
  charge_date: InvoiceFields.shape.charge_date,
  client_id: InvoiceFields.shape.client_id,
  client_code: InvoiceFields.shape.client_code,
  client_name: InvoiceFields.shape.client_name,
  client_name_en: InvoiceFields.shape.client_name_en,
  client_zipcode: InvoiceFields.shape.client_zipcode,
  client_address1: InvoiceFields.shape.client_address1,
  client_address2: InvoiceFields.shape.client_address2,
  payer_mark: InvoiceFields.shape.payer_mark,

  invoice_no: InvoiceFields.shape.invoice_no, // kikan INV NO.
  job_code: InvoiceFields.shape.job_code, // kikan 台帳番号
  charge_pay_day: InvoiceFields.shape.charge_pay_day, // kikan 支払期限

  vessel_voyage: InvoiceFields.shape.vessel_voyage,
  transport_route: InvoiceFields.shape.transport_route,
  ata: InvoiceFields.shape.ata,
  quantity_packages: InvoiceFields.shape.quantity_packages,
  customer_no: InvoiceFields.shape.customer_no,
  conversion_rate: InvoiceFields.shape.conversion_rate,
  conversion_rate_date: InvoiceFields.shape.conversion_rate_date,
  language: InvoiceFields.shape.language,
  invoice_status: InvoiceFields.shape.invoice_status,
  salesperson_name: InvoiceFields.shape.salesperson_name,

  booking_inquiry: InvoiceFields.shape.booking_inquiry,
  invoice_id_list: InvoiceFields.shape.invoice_id_list,
});

export type PostInvoice = z.infer<typeof PostInvoice>;

export const PatchInvoice = z
  .object({
    house_bl_number: InvoiceFields.shape.house_bl_number,
    charge_date: InvoiceFields.shape.charge_date,
    client_id: InvoiceFields.shape.client_id,
    client_code: InvoiceFields.shape.client_code,
    client_name: InvoiceFields.shape.client_name,
    client_name_en: InvoiceFields.shape.client_name_en,
    client_zipcode: InvoiceFields.shape.client_zipcode,
    client_address1: InvoiceFields.shape.client_address1,
    client_address2: InvoiceFields.shape.client_address2,
    payer_mark: InvoiceFields.shape.payer_mark,

    invoice_no: InvoiceFields.shape.invoice_no, // kikan INV NO.
    job_code: InvoiceFields.shape.job_code, // kikan 台帳番号
    charge_pay_day: InvoiceFields.shape.charge_pay_day, // kikan 支払期限

    vessel_voyage: InvoiceFields.shape.vessel_voyage,
    transport_route: InvoiceFields.shape.transport_route,
    ata: InvoiceFields.shape.ata,
    quantity_packages: InvoiceFields.shape.quantity_packages,
    customer_no: InvoiceFields.shape.customer_no,
    conversion_rate: InvoiceFields.shape.conversion_rate,
    conversion_rate_date: InvoiceFields.shape.conversion_rate_date,
    language: InvoiceFields.shape.language,
    invoice_status: InvoiceFields.shape.invoice_status,
    salesperson_name: InvoiceFields.shape.salesperson_name,

    invoice_id_list: InvoiceFields.shape.invoice_id_list,
  })
  .partial();

export type PatchInvoice = z.infer<typeof PatchInvoice>;

export const InvoiceChargePayDayForm = z
  .object({
    house_bl_number: InvoiceFields.shape.house_bl_number,
    charge_date: InvoiceFields.shape.charge_date,
    client_id: InvoiceFields.shape.client_id.min(1, invoiceStrings.errorClientIdNotSelected),
    client_code: InvoiceFields.shape.client_code.min(1),
    client_name: InvoiceFields.shape.client_name.min(1),
    payer_mark: InvoiceFields.shape.payer_mark,
    client_name_en: InvoiceFields.shape.client_name_en,
    client_zipcode: InvoiceFields.shape.client_zipcode.min(1),
    client_address1: InvoiceFields.shape.client_address1.min(1),
    client_address2: InvoiceFields.shape.client_address2.min(1),
  })
  .superRefine((v, ctx) => {
    if (!v.client_id && v.client_name) {
      ctx.addIssue({
        code: 'custom',
        message: invoiceStrings.errorClientIdNotSelected,
        path: ['client_name'],
      });
    }

    if (v.client_id && !v.client_code) {
      ctx.addIssue({
        code: 'custom',
        message: invoiceStrings.errorClientCodeEmpty,
        path: ['client_name'],
      });
    }
  });

export type InvoiceChargePayDayForm = z.infer<typeof InvoiceChargePayDayForm>;

export const InvoiceInfoForm = InvoiceFields.pick({
  invoice_no: true,
  job_code: true,
  charge_pay_day: true,
  vessel_voyage: true,
  transport_route: true,
  ata: true,
  quantity_packages: true,
  customer_no: true,
  conversion_rate: true,
  conversion_rate_date: true,
  language: true,
});

export type InvoiceInfoForm = z.infer<typeof InvoiceInfoForm>;

export const InvoiceChargePayDayQuery = z.object({
  charge_date: InvoiceFields.shape.charge_date,
  /**
   * `member_no` of the company
   */
  charge_client_code: z.string(),
  d2d_no: z.string(),
});

export const InvoiceChargePayDayResponse = InvoiceFields.pick({
  charge_pay_day: true,
  job_code: true,
  invoice_no: true,
});

export type InvoiceChargePayDayResponse = z.infer<typeof InvoiceChargePayDayResponse>;

export const InvoiceErrorMessage = z.object({
  status: z.string(),
  message: z.string(),
  invoice: z.object({
    id: z.number(),
  }),
});

export type InvoiceErrorMessage = z.infer<typeof InvoiceErrorMessage>;
